/**
 * TextField Component - Text input field with various types
 */

import React, { useCallback } from 'react';
import { TextFieldProps } from '../../../types/forms';
import { BaseField } from './BaseField';

export const TextField: React.FC<TextFieldProps> = ({
  name,
  label,
  placeholder,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  type = 'text',
  value,
  maxLength,
  pattern,
  autoComplete,
  onChange,
  onBlur,
  onFocus,
}) => {
  const fieldId = `field-${name}`;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    },
    [onChange]
  );

  const inputClasses = `
    w-full px-3 py-2
    border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    ${error ? 'border-red-300' : 'border-gray-300'}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
    ${readonly ? 'bg-gray-50' : ''}
    transition-colors duration-200
  `;

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <input
        id={fieldId}
        name={name}
        type={type}
        value={value || ''}
        placeholder={placeholder}
        maxLength={maxLength}
        pattern={pattern}
        autoComplete={autoComplete}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        className={inputClasses}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        aria-invalid={!!error}
        aria-describedby={error ? `${fieldId}-error` : undefined}
      />
      {maxLength && (
        <div className="absolute right-2 top-2 text-xs text-gray-500">
          {value?.length || 0}/{maxLength}
        </div>
      )}
    </BaseField>
  );
};

// Specialized text field variants
export const EmailField: React.FC<Omit<TextFieldProps, 'type'>> = (props) => (
  <TextField {...props} type="email" autoComplete="email" />
);

export const PasswordField: React.FC<Omit<TextFieldProps, 'type'>> = (props) => (
  <TextField {...props} type="password" autoComplete="current-password" />
);

export const TelField: React.FC<Omit<TextFieldProps, 'type'>> = (props) => (
  <TextField {...props} type="tel" autoComplete="tel" />
);

export const UrlField: React.FC<Omit<TextFieldProps, 'type'>> = (props) => (
  <TextField {...props} type="url" autoComplete="url" />
);

export default TextField;